import React, { useState, useRef, KeyboardEvent, useEffect } from "react";
import UserInfo from "../../../../interfaces/UserInfo";
import Avatar from "../../../../interfaces/avatar/Avatar";
import {
  TaskCommentMention,
  TaskCommentRequest,
} from "../../../../interfaces/taskAction/TaskCommentRequest";
import EmojiPicker, {
  EmojiItem,
  emojiData,
} from "../../emojiPicker/EmojiPicker";
import LoadingSpinner from "../../../ui/Loading/LoadingSpinner";
import sendmessageicon from "../../../../assets/images/sendmessageicon.svg";
import "./ChatInput.css";
import { Smile } from "lucide-react";

interface ChatInputProps {
  taskId: string;
  users: UserInfo[];
  avatars: Avatar[];
  onSubmit: (commentData: TaskCommentRequest) => Promise<void>;
}

const findAvatarById = (
  userId: string | undefined,
  avatars: Avatar[] | null
): string | null => {
  if (userId && avatars) {
    const avatarObj = avatars.find((avatar) => avatar.id === userId);
    return avatarObj ? `data:image/png;base64,${avatarObj.avatar}` : null;
  }
  return null;
};

export const getMessageContent = (element: HTMLElement): string => {
  let message = "";
  element.childNodes.forEach((node) => {
    if (node.nodeType === Node.TEXT_NODE) {
      message += node.textContent;
    } else if (node.nodeType === Node.ELEMENT_NODE) {
      const el = node as HTMLElement;
      if (el.tagName.toLowerCase() === "span" && el.getAttribute("data-tag")) {
        message += el.getAttribute("data-tag");
      } else if (el.tagName.toLowerCase() === "br") {
        message += "\n";
      } else {
        message += getMessageContent(el);
      }
    }
  });
  return message;
};

const ChatInput: React.FC<ChatInputProps> = ({
  taskId,
  users,
  avatars,
  onSubmit,
}) => {
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [isTagging, setIsTagging] = useState<boolean>(false);
  const [tagSearch, setTagSearch] = useState<string>("");
  const [suggestedUsers, setSuggestedUsers] = useState<UserInfo[]>([]);
  const [highlightedIndex, setHighlightedIndex] = useState<number>(0);
  const [showEmojiPicker, setShowEmojiPicker] = useState<boolean>(false);
  const [isEmojiTagging, setIsEmojiTagging] = useState<boolean>(false);
  const [emojiTagSearch, setEmojiTagSearch] = useState<string>("");
  const [suggestedEmojis, setSuggestedEmojis] = useState<EmojiItem[]>([]);
  const [highlightedEmojiIndex, setHighlightedEmojiIndex] = useState<number>(0);
  const [commentText, setCommentText] = useState("");
  const commentRef = useRef<HTMLDivElement>(null);
  const [savedRange, setSavedRange] = useState<Range | null>(null);

  useEffect(() => {
    if ((isTagging || isEmojiTagging) && savedRange) {
      const sel = window.getSelection();
      sel?.removeAllRanges();
      sel?.addRange(savedRange);
    }
  }, [isTagging, isEmojiTagging, savedRange]);

  const saveSelection = () => {
    const sel = window.getSelection();
    if (sel && sel.rangeCount > 0) {
      const range = sel.getRangeAt(0);
      if (commentRef.current?.contains(range.commonAncestorContainer)) {
        setSavedRange(range.cloneRange());
      }
    }
  };

  const restoreSelection = () => {
    if (savedRange) {
      const sel = window.getSelection();
      sel?.removeAllRanges();
      sel?.addRange(savedRange);
    }
  };

  const insertAtCursor = (html: string) => {
    if (!commentRef.current) return;
    commentRef.current.focus();
    restoreSelection();

    const sel = window.getSelection();
    if (sel && sel.getRangeAt(0)) {
      let range = sel.getRangeAt(0);
      range.deleteContents();
      const el = document.createElement("div");
      el.innerHTML = html;
      const frag = document.createDocumentFragment();
      let node, lastNode;
      while ((node = el.firstChild)) {
        lastNode = frag.appendChild(node);
      }
      range.insertNode(frag);

      if (lastNode) {
        range = range.cloneRange();
        range.setStartAfter(lastNode);
        range.collapse(true);
        sel.removeAllRanges();
        sel.addRange(range);
      }
      setCommentText(getMessageContent(commentRef.current));
      saveSelection();
    }
  };

  const handleInput = () => {
    if (!commentRef.current) return;
    saveSelection();
    const sel = window.getSelection();
    const range = sel?.getRangeAt(0);
    if (!range) return;

    const textNode = range.startContainer;
    const textContent = textNode.textContent || "";
    const caretOffset = range.startOffset;
    const textBeforeCaret = textContent.slice(0, caretOffset);

    const lastAt = textBeforeCaret.lastIndexOf("@");
    const lastColon = textBeforeCaret.lastIndexOf(":");
    const lastSpace = Math.max(
      textBeforeCaret.lastIndexOf(" "),
      textBeforeCaret.lastIndexOf("\n")
    );

    if (lastAt > lastSpace) {
      const search = textBeforeCaret.slice(lastAt + 1).toLowerCase();
      setIsTagging(true);
      setIsEmojiTagging(false);
      setTagSearch(search);
      const filtered = users.filter(
        (u) =>
          u.displayName.toLowerCase().startsWith(search) ||
          u.firstName.toLowerCase().startsWith(search) ||
          u.lastName.toLowerCase().startsWith(search)
      );
      setSuggestedUsers(filtered);
      setHighlightedIndex(0);
    } else {
      setIsTagging(false);
    }

    if (lastColon > lastSpace) {
      const search = textBeforeCaret.slice(lastColon + 1);
      if (search) {
        setIsEmojiTagging(true);
        setIsTagging(false);
        setEmojiTagSearch(search);
        const filtered = emojiData.filter((e) =>
          e.keywords.some((k) =>
            k.toLowerCase().startsWith(search.toLowerCase())
          )
        );
        setSuggestedEmojis(filtered);
        setHighlightedEmojiIndex(0);
      } else {
        setIsEmojiTagging(false);
      }
    } else {
      setIsEmojiTagging(false);
    }

    setCommentText(getMessageContent(commentRef.current));
  };

  const handleTagSelection = (user: UserInfo) => {
    if (!commentRef.current) return;
    const tagHtml = `<span contenteditable="false" class="chat-input__mention-badge" data-tag="{user:${user.id}}" spellcheck="false">${user.displayName}</span>&nbsp;`;

    restoreSelection();
    const sel = window.getSelection();
    const range = sel?.getRangeAt(0);

    if (range && range.startContainer.nodeType === Node.TEXT_NODE) {
      const textNode = range.startContainer as Text;
      const textContent = textNode.textContent || "";
      const caretOffset = range.startOffset;
      const lastAt = textContent.slice(0, caretOffset).lastIndexOf("@");

      if (lastAt !== -1) {
        range.setStart(textNode, lastAt);
        range.deleteContents();
        insertAtCursor(tagHtml);
      }
    }
    setIsTagging(false);
  };
  
  const handleEmojiTagSelection = (emoji: string) => {
    if (!commentRef.current) return;

    restoreSelection();
    const sel = window.getSelection();
    const range = sel?.getRangeAt(0);

    if (range && range.startContainer.nodeType === Node.TEXT_NODE) {
      const textNode = range.startContainer as Text;
      const textContent = textNode.textContent || "";
      const caretOffset = range.startOffset;
      const lastColon = textContent.slice(0, caretOffset).lastIndexOf(":");

      if (lastColon !== -1) {
        range.setStart(textNode, lastColon);
        range.deleteContents();
        insertAtCursor(emoji);
      }
    }
    setIsEmojiTagging(false);
  };

  const handleKeyDown = (e: KeyboardEvent<HTMLDivElement>) => {
    const suggestionsActive = isTagging && suggestedUsers.length > 0;
    const emojiSuggestionsActive = isEmojiTagging && suggestedEmojis.length > 0;

    if (suggestionsActive) {
      if (e.key === "ArrowDown") {
        e.preventDefault();
        setHighlightedIndex((prev) => (prev + 1) % suggestedUsers.length);
      } else if (e.key === "ArrowUp") {
        e.preventDefault();
        setHighlightedIndex(
          (prev) => (prev - 1 + suggestedUsers.length) % suggestedUsers.length
        );
      } else if (e.key === "Enter" || e.key === "Tab") {
        e.preventDefault();
        handleTagSelection(suggestedUsers[highlightedIndex]);
      } else if (e.key === "Escape") {
        e.preventDefault();
        setIsTagging(false);
      }
    } else if (emojiSuggestionsActive) {
      if (e.key === "ArrowDown") {
        e.preventDefault();
        setHighlightedEmojiIndex((prev) => (prev + 1) % suggestedEmojis.length);
      } else if (e.key === "ArrowUp") {
        e.preventDefault();
        setHighlightedEmojiIndex(
          (prev) => (prev - 1 + suggestedEmojis.length) % suggestedEmojis.length
        );
      } else if (e.key === "Enter" || e.key === "Tab") {
        e.preventDefault();
        handleEmojiTagSelection(suggestedEmojis[highlightedEmojiIndex].emoji);
      } else if (e.key === "Escape") {
        e.preventDefault();
        setIsEmojiTagging(false);
      }
    } else {
      if (e.key === "Enter" && !e.shiftKey) {
        e.preventDefault();
        handleSubmit();
      }
    }
  };

  const handleSubmit = async () => {
    if (
      isLoading ||
      !commentRef.current ||
      commentRef.current.innerText.trim() === ""
    )
      return;
    setIsLoading(true);
    const messageContent = getMessageContent(commentRef.current);
    const mentions: TaskCommentMention[] = [];
    const regex = /{user:(.+?)}/g;
    let match;
    while ((match = regex.exec(messageContent)) !== null) {
      mentions.push({
        mentionedEntityType: "user",
        mentionedEntityId: match[1],
      });
    }
    const commentData: TaskCommentRequest = {
      taskId: parseInt(taskId),
      description: messageContent,
      mentions,
    };
    await onSubmit(commentData);
    if (commentRef.current) commentRef.current.innerHTML = "";
    setCommentText("");
    setIsLoading(false);
  };

  return (
    <div className="chat-input">
      <div className="chat-input__header">
        <h3 className="chat-input__title">Add Comment</h3>
      </div>

      <div className="chat-input__body">
        <div className="chat-input__container">
          <div className="chat-input__textarea-wrapper">
            <div
              className="chat-input__textarea"
              contentEditable
              onInput={handleInput}
              onBlur={saveSelection}
              onFocus={restoreSelection}
              onKeyDown={handleKeyDown}
              ref={commentRef}
            ></div>
            {!commentText && (
              <div className="chat-input__placeholder">Write a comment...</div>
            )}
            
            <div className="chat-input__actions">
              <div className="flex items-center gap-1">
                <div className="chat-input__emoji-btn-wrapper">
                  <button
                    className="chat-input__emoji-btn"
                    onMouseDown={(e) => e.preventDefault()}
                    onClick={() => setShowEmojiPicker(!showEmojiPicker)}
                    type="button"
                  >
                    <Smile />
                  </button>
                  {showEmojiPicker && (
                    <div className="chat-input__emoji-picker">
                      <EmojiPicker
                        onSelect={(emoji) => {
                          insertAtCursor(emoji);
                          setShowEmojiPicker(false);
                        }}
                      />
                    </div>
                  )}
                </div>

                <button
                  className="btn btn-primary submit-btn"
                  onClick={handleSubmit}
                  disabled={isLoading || !commentText.trim()}
                  type="button"
                >
                  {isLoading ? (
                    <LoadingSpinner text={false} w={4} h={4} />
                  ) : (
                    "Post Comment"
                  )}
                </button>
              </div>
            </div>
          </div>

          {isTagging && suggestedUsers.length > 0 && (
            <div className="chat-input__suggestions">
              {suggestedUsers.map((user, index) => {
                const avatarUrl = findAvatarById(user.id, avatars);
                return (
                  <div
                    key={user.id}
                    onMouseDown={(e) => e.preventDefault()}
                    className={`chat-input__suggestion-item ${
                      index === highlightedIndex
                        ? "chat-input__suggestion-item--active"
                        : ""
                    }`}
                    onClick={() => handleTagSelection(user)}
                  >
                    <div className="chat-input__suggestion-avatar">
                      {avatarUrl ? (
                        <img src={avatarUrl} alt="avatar" />
                      ) : (
                        `${user.firstName[0]}${user.lastName[0]}`
                      )}
                    </div>
                    <span className="chat-input__suggestion-name">
                      {user.displayName}
                    </span>
                  </div>
                );
              })}
            </div>
          )}

          {isEmojiTagging && suggestedEmojis.length > 0 && (
            <div className="chat-input__suggestions">
              {suggestedEmojis.map((item, index) => (
                <div
                  key={item.keywords[0]}
                  onMouseDown={(e) => e.preventDefault()}
                  className={`chat-input__suggestion-item ${
                    index === highlightedEmojiIndex
                      ? "chat-input__suggestion-item--active"
                      : ""
                  }`}
                  onClick={() => handleEmojiTagSelection(item.emoji)}
                >
                  <span className="chat-input__emoji">{item.emoji}</span>
                  <span className="chat-input__suggestion-name">
                    {item.keywords[0]}
                  </span>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ChatInput;
